import React from 'react';

import {ScoresListWidgetController} from "../scores-list-widget.controller";
import {ACTION_TYPES, FETCHING_STATES, COMPONENT_STATES} from "../../../core/consts";
import {Loader} from "../../../core/components/loading/loader";
import {ScoresListWidgetProps} from "../models/scores-list-widget.props";
import {Compose, ComposeSettings} from "../../../core/components/composers/compose";
import {DashboardLinkModel} from "../../../containers/dashboard-container/models/dashboard-container-link-model";
import {TrapezoidCardTitle} from "../../../core/components/misc/titles/trapezoid-card-title";
import {SportTypeEmptyState} from "../../../core/components/scores-placeholders/empty-state/sport-type-empty.state";
import {ScoresLiveEmptyState} from "../../../core/components/scores-placeholders/empty-state/scores.live-empty-state";
import {APP} from "../../../core/app/consts";
import {ScoresListWidgetCompetitions} from "./scores-list-widget-competitions";
import {ScoresListWidgetShowAllButton, ScoresListWidgetShowAllButtonProps} from "./scores-list-widget.show-all-button";
import {SCORES_LIST_WIDGET} from "../scores-list-widget.consts";
import {IntersectionObserver} from "../../../core/components/intersection-observer/intersection-observer";
import {ScoresListHeaderModule} from "../../../modules/scores-list-header-module/components/scores-list-header-module";
import {ScoresListHeaderModuleProps} from "../../../modules/scores-list-header-module/models/scores-list-header-module.props";
import {ScoresPlaceholder} from "../../../core/components/scores-placeholders/placeholder/scores.placeholder";
import {GamesPredictionsWidgetAsync} from "../../games-predictions-widget/components/games-predictions-widget.async";
import classnames from "classnames";
import {ScoresEmptyStateLiveRequestParams} from "../../../core/components/scores-placeholders/empty-state/assest/scores-empty-state.live.request-params";

export class ScoresListWidgetComponentProps extends ScoresListWidgetProps {}

export const ScoresListWidgetComponent = (props: ScoresListWidgetComponentProps) => {
  const {state, store} = props.controller;

  const handleDateChange = (date: Date) => {
    if(state.isToday) {
      state.isLive = false
    }
    state.filterDate = date;
    store.setComponentState(COMPONENT_STATES.loading);
    store.clearGames();
    props.controller.action(ACTION_TYPES.fetch);
  };

  const handleLiveSwitch = (isActive: Boolean) => {
    props.controller.action(ACTION_TYPES.liveSwitchClick, {
      source: APP.ANALYTICS.SOURCES.scores,
      isLive: isActive,
      skipNavigation: true
    });
    state.isLive = isActive;
    store.setComponentState(COMPONENT_STATES.loading);
    store.clearGames();
    props.controller.action(ACTION_TYPES.fetch);
  };

  const loaderProps = {
    trigger: state.isLive,
    when: state.fetchingState === FETCHING_STATES.fetching,
    Component: ScoresPlaceholder,
    componentProps: {
      shouldHideHeader: true
    }
  };

  const scoresHeaderModuleProps: ScoresListHeaderModuleProps = {
    location: props.location,
    date: state.filterDate,
    isLive: state.isLive,
    toRenderLiveSwitch: state.isToday,
    liveGamesCount: state.liveGamesCount,
    linkType: DashboardLinkModel,
    toRenderCalendar: true,
    prevTooltip: state.isToday ? state.terms.DATER_PREV_DAY : state.terms.DATER_PREV_DAY_NOT_YESTERDAY,
    nextTooltip: state.isToday ? state.terms.DATER_NEXT_DAY : state.terms.DATER_NEXT_DAY_NOT_TOMORROW,
    nextLinkModel: null,
    previousLinkModel: null,
    onDateChange: handleDateChange,
    onClickPrevious: () => {
      const prevDate: Date = state.datetimeUtils.addDays(state.filterDate, -1);
      handleDateChange(prevDate);
    },
    onClickNext: () => {
      const nextDate: Date = state.datetimeUtils.addDays(state.filterDate, 1);
      handleDateChange(nextDate);
    },
    switchOnClick: handleLiveSwitch
  };

  const scoresListShowAllButtonProps: ScoresListWidgetShowAllButtonProps = {
    onClick: () => props.controller.action(SCORES_LIST_WIDGET.ACTION_TYPES.showAll),
    label: state.terms.WEB_ALL_SCORES_SHOW_ALL_BUTTON
  };

  const gamesPredictionsProps = {
    className: 'scores-list-widget-games-predictions',
    context: APP.CONTEXT.allScores
  };

  const emptyStateProps = {
    sportTypeId: state.sportTypeId,
    isSmallView: props.controller.device.isSmallView,
    text: state.terms.WEB_NO_CONTENT_GAMES,
    className: classnames('scores-list-widget-empty-state')
  }

  const liveEmptyState = {
    linkModel: state.allScoresLinkModel,
    text: state.terms.LIVE_MATCHES_EMPTY_STATE,
    buttonText: state.terms.WEB_MY_SCORES_SEE_ALL_MATCHES,
    isLargeView: props.controller.device.isLargeView,
    ImageSrc: ScoresEmptyStateLiveRequestParams
  }

  const isLoaded: Boolean = state.componentState === COMPONENT_STATES.loaded;
  const hasNoGames: Boolean = state.gamesByCompetition.length === 0;
  const showEmptyState: Boolean = isLoaded && hasNoGames && !state.isLive;
  const showLiveEmptyState: Boolean = isLoaded && hasNoGames && state.isLive;
  const showAllButton: Boolean = isLoaded && state.shouldRenderShowAllButton;

  return (
    <div className={props.controller.device.isSmallView ? "scores-list-mobile" : "scores-list-desktop"}>
      <div className={'scores-list-widget-header-title'}>
        {
          props.controller.device.isLargeView && <TrapezoidCardTitle title={state.tabTitle}/>
        }
      </div>
      <ScoresListHeaderModule {...scoresHeaderModuleProps} />
      <Loader {...loaderProps}>
        {
          showEmptyState && <SportTypeEmptyState {...emptyStateProps}/>
        }
        {
          showLiveEmptyState && <ScoresLiveEmptyState {...liveEmptyState}/>
        }
        <ScoresListWidgetCompetitions {...props} />
        {
          showAllButton && <ScoresListWidgetShowAllButton {...scoresListShowAllButtonProps}/>
        }
        <IntersectionObserver>
          {
            state.isGamesPredictionsVisible && <GamesPredictionsWidgetAsync {...gamesPredictionsProps}/>
          }
        </IntersectionObserver>
      </Loader>
    </div>
  )
};

const settings: ComposeSettings = {
  component: ScoresListWidgetComponent,
  propsType: ScoresListWidgetComponentProps,
  controllerType: ScoresListWidgetController,
  shouldTriggerOnRenderComplete: true,

  LoaderComponent: (placeholderProps) => <ScoresPlaceholder {...placeholderProps} />,


  createModule: true,
  observe: true
};

export const ScoresListWidget = Compose(settings);
